import { useState, useEffect, useCallback, useRef } from "react";
import { forecastService } from "../../services/forecastService";
import { useReduxStore } from "../../hooks/useReduxStore";
import { updateAuth } from "../../store/Reducers/authReducer";
import { toForecastApiString } from "../../services/locationManager";
import {
  extractTodaySunTimesFromDailyForecast,
  findTodayDailyPeriod,
  mapPeriodToCurrentWeather,
  mapSevenDayForecastRows,
  mapTodayForecastToCurrent,
} from "../../services/forecastService";
import { getSunTimes } from "../../utils/suncalcLite";

const DEFAULT_COORDS = { lat: 44.9777, lng: -93.276 }; // Minneapolis ~55401

function normalizeLatLng(lat, lng) {
  const la = Number(lat);
  const ln = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return null;
  if (Math.abs(la) <= 90 && Math.abs(ln) <= 180) return { lat: la, lng: ln };
  if (Math.abs(ln) <= 90 && Math.abs(la) <= 180) return { lat: ln, lng: la };
  return { lat: la, lng: ln };
}

function coordsFromLocation(loc, place) {
  if (loc?.lat != null && loc?.lng != null) {
    const n = normalizeLatLng(loc.lat, loc.lng);
    if (n) return n;
  }
  const plat = place?.loc?.lat ?? place?.lat ?? place?.latitude;
  const plng = place?.loc?.long ?? place?.loc?.lon ?? place?.lng ?? place?.longitude;
  if (plat != null && plng != null) {
    const n = normalizeLatLng(plat, plng);
    if (n) return n;
  }
  return { ...DEFAULT_COORDS };
}

function buildNarrative(current, maxF, minF) {
  if (!current) return "";
  const desc =
    current.weather?.[0]?.description ||
    current.weatherPrimary ||
    "";
  const pop = typeof current.pop === "number" ? Math.round(current.pop) : 0;
  const wind =
    typeof current.windSpeedMPH === "number"
      ? `${Math.round(current.windSpeedMPH)} mph`
      : null;
  const hi =
    typeof maxF === "number" && maxF !== current.tempF
      ? `High near ${Math.round(maxF)}°F`
      : "";
  const lo =
    typeof minF === "number" && minF !== current.tempF
      ? `low near ${Math.round(minF)}°F`
      : "";
  const hl = [hi, lo].filter(Boolean).join(" and ");
  const head = desc
    ? `${desc.charAt(0).toUpperCase()}${desc.slice(1)}. `
    : "";
  const windPart = wind ? `Winds ${wind}. ` : "";
  return `${head}${hl ? `${hl}. ` : ""}${windPart}${pop}% chance of precipitation.`;
}

const FETCH_RETRY_ATTEMPTS = 2;
const FETCH_RETRY_DELAY_MS = 800;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchWithRetry(label, fn) {
  let lastErr;
  for (let attempt = 1; attempt <= FETCH_RETRY_ATTEMPTS; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (attempt < FETCH_RETRY_ATTEMPTS) {
        await sleep(FETCH_RETRY_DELAY_MS * attempt);
      }
    }
  }
  throw lastErr;
}

/** Wind chart series from today's daily period + current (no hourly API). */
function buildWindSeriesFromDaily(current, dailyPeriod) {
  const base =
    typeof current?.windSpeedMPH === "number"
      ? current.windSpeedMPH
      : typeof dailyPeriod?.windSpeedMPH === "number"
        ? dailyPeriod.windSpeedMPH
        : null;
  if (base == null) return [];

  const lo =
    typeof dailyPeriod?.windSpeedMinMPH === "number"
      ? dailyPeriod.windSpeedMinMPH
      : base;
  const hi =
    typeof dailyPeriod?.windSpeedMaxMPH === "number"
      ? dailyPeriod.windSpeedMaxMPH
      : typeof dailyPeriod?.windGustMPH === "number"
        ? dailyPeriod.windGustMPH
        : base;

  const n = 24;
  return Array.from({ length: n }, (_, i) => {
    const t = i / Math.max(n - 1, 1);
    const wave = Math.sin(t * Math.PI);
    return Math.round(lo + (hi - lo) * wave);
  });
}

/** Only refetch when coordinates change — not when city name is filled in after geocode. */
function locationFetchKey(loc) {
  if (!loc || loc.lat == null || loc.lng == null) return "default";
  const lat = Number(loc.lat);
  const lng = Number(loc.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "default";
  return `${lat.toFixed(4)},${lng.toFixed(4)}`;
}

export default function useDashboardWeather(locationParam) {
  const locationKey = locationFetchKey(locationParam);
  const { dispatch } = useReduxStore();
  const fetchGenerationRef = useRef(0);
  const [current, setCurrent] = useState(null);
  const [place, setPlace] = useState(null);
  const [hourlyWindMph, setHourlyWindMph] = useState([]);
  const [tempHighF, setTempHighF] = useState(null);
  const [tempLowF, setTempLowF] = useState(null);
  const [sunrise, setSunrise] = useState(null);
  const [sunset, setSunset] = useState(null);
  const [sunTimeZone, setSunTimeZone] = useState(null);
  const [sunriseIso, setSunriseIso] = useState(null);
  const [sunsetIso, setSunsetIso] = useState(null);
  const [narrative, setNarrative] = useState("");
  const [dailyForecastRaw, setDailyForecastRaw] = useState(null);
  const [weekMinF, setWeekMinF] = useState(null);
  const [weekMaxF, setWeekMaxF] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    const generation = ++fetchGenerationRef.current;
    const isStale = () => generation !== fetchGenerationRef.current;

    try {
      setLoading(true);
      setError(null);
      const locStr = toForecastApiString(locationParam);

      const [dailyRes, currentRes] = await Promise.all([
        fetchWithRetry("daily-forecast", () =>
          forecastService.getNext7DaysForecast(locStr)
        ),
        // Dedicated Xweather current-conditions endpoint — used for the
        // Current Weather card's live fields. Non-fatal if it fails: we
        // fall back to the forecast-derived value below.
        fetchWithRetry("current-weather", () =>
          forecastService.getCurrentWeather(locStr)
        ).catch((err) => {
          console.warn(
            "Current weather (conditions) fetch failed, falling back to forecast-derived current:",
            err
          );
          return null;
        }),
      ]);

      if (isStale()) return;

      const dailyBlock =
        dailyRes?.response?.[0] ?? dailyRes?.data?.response?.[0] ?? null;
      const dailyPeriods = dailyBlock?.periods ?? [];
      const locationTz = dailyBlock?.profile?.tz || null;
      const dailyP = findTodayDailyPeriod(dailyPeriods, new Date(), locationTz);

      // Current Weather card: prefer the dedicated current-conditions
      // response (accurate, real-time) over the 7-day forecast's "today"
      // period. High/Low still come from the forecast below since the
      // conditions endpoint has no daily min/max.
      const currentBlock =
        currentRes?.response?.[0] ?? currentRes?.data?.response?.[0] ?? null;
      const currentPeriod = currentBlock?.periods?.[0] ?? null;

      let cur = null;
      let pl = null;
      if (currentPeriod) {
        const mapped = mapPeriodToCurrentWeather(
          currentPeriod,
          currentBlock?.place ?? null
        );
        cur = mapped.current;
        pl = mapped.place;
      }
      if (!cur) {
        const fallback = mapTodayForecastToCurrent(dailyRes);
        cur = fallback.current;
        pl = pl || fallback.place;
      }
      if (!pl) pl = dailyBlock?.place ?? null;

      setCurrent(cur);
      setPlace(pl);
      if (pl) dispatch(updateAuth({ place: pl }));

      setHourlyWindMph(buildWindSeriesFromDaily(cur, dailyP));

      let maxF =
        dailyP?.maxTempF ??
        dailyP?.tempMaxF ??
        (cur?.tempF != null ? cur.tempF : null);
      let minF =
        dailyP?.minTempF ??
        dailyP?.tempMinF ??
        (cur?.tempF != null ? cur.tempF : null);
      if (maxF == null && cur?.tempF != null) maxF = cur.tempF;
      if (minF == null && cur?.tempF != null) minF = cur.tempF;
      if (maxF != null && minF != null && maxF < minF) {
        const t = maxF;
        maxF = minF;
        minF = t;
      }
      setTempHighF(maxF);
      setTempLowF(minF);

      const {
        sunrise: apiSunrise,
        sunset: apiSunset,
        timeZone: apiSunTz,
        sunriseIso: apiSunriseIso,
        sunsetIso: apiSunsetIso,
      } = extractTodaySunTimesFromDailyForecast(dailyRes);

      let sr = apiSunrise;
      let ss = apiSunset;
      let tz = apiSunTz || locationTz;

      setSunriseIso(apiSunriseIso);
      setSunsetIso(apiSunsetIso);

      if (!sr || !ss) {
        const { lat, lng } = coordsFromLocation(locationParam, pl);
        try {
          const t = getSunTimes(new Date(), lat, lng, 0);
          sr = sr ?? t.sunrise;
          ss = ss ?? t.sunset;
        } catch (e) {
          console.warn("Sun times fallback (suncalc):", e);
        }
      }

      setSunTimeZone(tz);
      setSunrise(sr instanceof Date && !Number.isNaN(sr.getTime()) ? sr : null);
      setSunset(ss instanceof Date && !Number.isNaN(ss.getTime()) ? ss : null);

      setNarrative(buildNarrative(cur, maxF, minF));
      setDailyForecastRaw(dailyRes);
      const weekTemps = mapSevenDayForecastRows(dailyRes);
      setWeekMinF(weekTemps.weekMinF);
      setWeekMaxF(weekTemps.weekMaxF);
    } catch (err) {
      if (isStale()) return;
      console.error("Dashboard weather fetch:", err);
      const timedOut =
        err?.message?.includes("timeout") ||
        err?.message?.includes("Timed out") ||
        err?.message?.includes("Busy");
      setError(
        timedOut
          ? "Weather server is slow. Please try refresh again in a moment."
          : err.message || "Failed to load weather"
      );
      setCurrent(null);
      setPlace(null);
      setHourlyWindMph([]);
      setDailyForecastRaw(null);
      setWeekMinF(null);
      setWeekMaxF(null);
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [locationParam, locationKey, dispatch]);

  useEffect(() => {
    fetchAll();
  }, [locationKey, fetchAll]);

  return {
    current,
    place,
    hourlyWindMph,
    tempHighF,
    tempLowF,
    sunrise,
    sunset,
    sunTimeZone,
    sunriseIso,
    sunsetIso,
    narrative,
    dailyForecastRaw,
    weekMinF,
    weekMaxF,
    loading,
    error,
    refetch: fetchAll,
  };
}
